import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ToastProvider";
import { post } from "@/lib/api";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

const GENRES = ["bluegrass", "americana", "folk", "all"];

export function InfluencerOutreachPanel() {
  const [genre, setGenre] = useState("bluegrass");
  const { data, isLoading, mutate } = useSWR(`/marketing/influencers/suggestions?genre=${genre}`, fetcher);
  const influencers = data?.influencers ?? [];
  const { push } = useToast();
  const [selectedId, setSelectedId] = useState<string>("");
  const [tone, setTone] = useState("friendly");
  const [context, setContext] = useState("Collaborating on upcoming tour content.");

  const handleOutreach = async () => {
    if (!selectedId) {
      push({ title: "Select influencer", message: "Choose an influencer to contact.", variant: "destructive" });
      return;
    }
    try {
      await post("/marketing/influencers/outreach", { influencer_id: selectedId, tone, context: { note: context } });
      push({ title: "Outreach queued", message: "AI draft ready for HITL review." });
      await mutate();
    } catch (error) {
      push({
        title: "Unable to queue outreach",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">Influencer Outreach</p>
          <p className="text-xs text-muted-foreground">Suggested collaborators by genre + geo clusters.</p>
        </div>
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger className="w-40 bg-background/50 text-foreground">
            <SelectValue placeholder="Genre" />
          </SelectTrigger>
          <SelectContent className="bg-background/40 text-foreground">
            {GENRES.map((entry) => (
              <SelectItem key={entry} value={entry}>
                {entry}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading suggestions…</p>
      ) : (
        <div className="space-y-2">
          {influencers.map((influencer: any) => (
            <label key={influencer.id} className="flex cursor-pointer justify-between rounded-xl border border-border/70 bg-background/40 p-3 text-sm text-foreground/80">
              <div>
                <p className="font-semibold text-foreground">{influencer.name}</p>
                <p className="text-xs text-muted-foreground">{influencer.genre} · {influencer.platform} · {influencer.city}</p>
                <p className="text-xs text-muted-foreground">{influencer.followers.toLocaleString()} followers · {influencer.engagement_rate}% engagement</p>
              </div>
              <input
                type="radio"
                className="h-4 w-4"
                checked={selectedId === influencer.id}
                onChange={() => setSelectedId(influencer.id)}
              />
            </label>
          ))}
          {!influencers.length ? <p className="text-xs text-muted-foreground">No suggestions for that genre.</p> : null}
        </div>
      )}

      <section className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Outreach context</p>
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="bg-background/50 text-foreground">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background/40 text-foreground">
            <SelectItem value="friendly">Friendly</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="playful">Playful</SelectItem>
          </SelectContent>
        </Select>
        <Textarea value={context} onChange={(event) => setContext(event.target.value)} rows={3} className="bg-background/40 text-foreground" />
        <Button onClick={() => void handleOutreach()} disabled={!selectedId}>
          Queue outreach
        </Button>
      </section>
    </Card>
  );
}
